/**
 * @nanokart/tryon-studio-sdk
 * NanokartTryOnStudio - Root component
 */

import React from 'react';

import { TryOnStudioConfig, CatalogItem } from './types/config';
import { TryOnStudioEvents } from './types/events';
import { HostAdapter } from './core/adapters/types';
import { CatalogProvider } from './core/providers/types';
import { TryOnProvider } from './core/TryOnProvider';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { StudioContainer } from './components/layout/StudioContainer';

// ============================================================================
// Props Interface
// ============================================================================

export interface NanokartTryOnStudioProps extends TryOnStudioConfig, TryOnStudioEvents {
  /**
   * Custom host adapter (optional).
   * Defaults to DefaultHostAdapter for standalone, ShopifyHostAdapter for Shopify.
   */
  hostAdapter?: HostAdapter;

  /**
   * Custom catalog provider (optional).
   * Use if you need advanced catalog fetching logic.
   * Otherwise use `items` for static catalog or `catalogUrl` for URL-based catalog.
   */
  catalogProvider?: CatalogProvider;

  /** CSS class for the container */
  className?: string;

  /** Inline styles for the container */
  style?: React.CSSProperties;

  /** Children for custom layouts */
  children?: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

/**
 * NanokartTryOnStudio — Drop-in virtual try-on experience.
 *
 * @example React
 * ```tsx
 * <NanokartTryOnStudio
 *   apiKey="nk_live_xxx"
 *   partnerId="your-partner-id"
 *   items={[{
 *     productId: '123',
 *     name: 'Blue Dress',
 *     imageUrl: 'https://...',
 *     price: 2999,
 *     currency: 'INR',
 *   }]}
 *   onTryOnComplete={(event) => console.log(event.payload.resultImageUrl)}
 *   onAddToCart={(event) => myCart.add(event.payload)}
 * />
 * ```
 *
 * @example URL-based catalog
 * ```tsx
 * <NanokartTryOnStudio
 *   apiKey="nk_live_xxx"
 *   partnerId="your-partner-id"
 *   catalogUrl="https://your-store.com/api/products"
 *   catalogHeaders={{ Authorization: 'Bearer your-token' }}
 *   onTryOnComplete={(event) => console.log(event)}
 * />
 * ```
 */
export const NanokartTryOnStudio: React.FC<NanokartTryOnStudioProps> = ({
  // Core required props
  apiKey,
  partnerId,

  // Optional config
  apiBaseUrl,
  items,
  catalogUrl,
  catalogHeaders,
  mapCatalogItem,
  currency = 'INR',
  timezone = 'Asia/Kolkata',
  mode = 'embedded-commerce',
  features,
  brandingConfig,
  platformContext = 'standalone',
  platformMetadata,

  // Event callbacks
  onReady,
  onTryOnComplete,
  onMomentGenerated,
  onAddToCart,
  onRemoveFromCart,
  onSessionStart,
  onSessionEnd,
  onError,
  onAnalyticsEvent,

  // Adapters
  hostAdapter,
  catalogProvider,

  // UI
  className,
  style,
  children,
}) => {
  const config: TryOnStudioConfig = {
    apiKey,
    partnerId,
    apiBaseUrl,
    items,
    catalogUrl,
    catalogHeaders,
    mapCatalogItem,
    currency,
    timezone,
    mode,
    features,
    brandingConfig,
    platformContext,
    platformMetadata,
  };

  const events: TryOnStudioEvents = {
    onReady,
    onTryOnComplete,
    onMomentGenerated,
    onAddToCart,
    onRemoveFromCart,
    onSessionStart,
    onSessionEnd,
    onError,
    onAnalyticsEvent,
  };

  const handleError = (error: Error) => {
    console.error('[NanokartTryOnStudio]', error);
    onError?.({
      sdkVersion: '2.0.0',
      schemaVersion: '2.0',
      eventName: 'error',
      correlationId: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      payload: {
        code: 'unknown/error',
        message: error.message,
        recoverable: false,
      },
    });
  };

  return (
    <ErrorBoundary onError={handleError}>
      <TryOnProvider
        config={config}
        events={events}
        hostAdapter={hostAdapter}
        catalogProvider={catalogProvider}
      >
        <StudioContainer className={className} style={style}>
          {children}
        </StudioContainer>
      </TryOnProvider>
    </ErrorBoundary>
  );
};

export default NanokartTryOnStudio;
